/**
 * Conversation history — every run, newest first, with cursor pagination and
 * URL-synced filters (?flowId=…&status=…) so per-flow history is linkable
 * (FlowsPage's "History" action lands here pre-filtered). Rows show what was
 * asked (the brief preview persisted by the BFF), who asked it, status, and a
 * deliverable-count chip; they route to the read-only RunView at
 * /conversations/:id. Pages accumulate locally behind a "Load more" button
 * while the BFF returns a nextCursor.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { PortalFlow, RunStatus, RunSummary } from '@portal/contracts'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { listFlows } from '../api/flows'
import { listRuns } from '../api/runs'
import { keys } from '../hooks/queryClient'
import { describeApiError, useI18n } from '../i18n'

const STATUS_ORDER: RunStatus[] = ['success', 'partial_success', 'failure', 'awaiting_user_response', 'running']
const STATUS_VALUES = new Set<string>(STATUS_ORDER)

/**
 * The notice above the list, if any. "No conversations yet" is said only when nothing is left
 * to load: a page can arrive empty with older ones behind it (an fr-docs that does not leave
 * internal runs out of its list makes the BFF read past them, and it stops after five pages),
 * and then the list says so beside "Load more" instead of claiming there is nothing.
 */
export function historyNotice(args: {
  loading: boolean
  rows: number
  hasNextPage: boolean
  filtered: boolean
}): 'emptyFiltered' | 'emptyNoConversations' | 'emptySoFar' | null {
  if (args.loading || args.rows > 0) return null
  if (args.hasNextPage) return 'emptySoFar'
  return args.filtered ? 'emptyFiltered' : 'emptyNoConversations'
}

export function ConversationsPage() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [params, setParams] = useSearchParams()

  const flowId = params.get('flowId') ?? ''
  const statusParam = params.get('status') ?? ''
  const status = STATUS_VALUES.has(statusParam) ? (statusParam as RunStatus) : undefined

  const flows = useQuery({ queryKey: keys.flows(), queryFn: listFlows })

  const query = useInfiniteQuery({
    queryKey: keys.conversations(`history:${flowId}:${status ?? ''}`),
    queryFn: ({ pageParam }) =>
      listRuns({
        limit: 20,
        cursor: pageParam,
        ...(flowId ? { flowId } : {}),
        ...(status ? { status } : {})
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor
  })

  const rows: RunSummary[] = query.data?.pages.flatMap((p) => p.items) ?? []
  const filtered = flowId || status
  const notice = historyNotice({
    loading: query.isLoading,
    rows: rows.length,
    hasNextPage: query.hasNextPage,
    filtered: Boolean(filtered)
  })
  // Prefer the flow catalog's display name over the humanized flowId fallback
  // the BFF puts on rows, so list rows and the filter dropdown agree.
  const flowNames = new Map((flows.data ?? []).map((f) => [f.flowId, f.displayName]))

  const setFilter = (key: 'flowId' | 'status', value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  return (
    <div className="page portal-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t.runs.historyTitle}</h1>
          <div className="page-sub">{t.runs.historySubtitle}</div>
        </div>
      </header>

      <div className="history-filters">
        <select
          className="select"
          aria-label={t.runs.filterByFlow}
          value={flowId}
          onChange={(e) => setFilter('flowId', e.target.value)}
        >
          <option value="">{t.runs.allFlows}</option>
          {(flows.data ?? []).map((f: PortalFlow) => (
            <option key={f.flowId} value={f.flowId}>
              {f.displayName}
            </option>
          ))}
          {/* Keep an unknown flowId selectable so deep links to retired flows still filter. */}
          {flowId && !(flows.data ?? []).some((f) => f.flowId === flowId) && <option value={flowId}>{flowId}</option>}
        </select>
        <select
          className="select"
          aria-label={t.runs.filterByStatus}
          value={status ?? ''}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="">{t.runs.anyStatus}</option>
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {t.runs.statusLabel(s)}
            </option>
          ))}
        </select>
        {filtered && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setParams({}, { replace: true })}>
            <Icon name="x" size={12} /> {t.runs.clearFilters}
          </button>
        )}
      </div>

      {query.isLoading && (
        <div className="portal-loading">
          <Spinner size={18} /> {t.runs.loadingHistory}
        </div>
      )}

      {query.isError && <Notice kind="danger">{describeApiError(t, query.error)}</Notice>}

      {notice && <Notice kind="info">{t.runs[notice]}</Notice>}

      {rows.length > 0 && (
        <div className="run-list">
          {rows.map((r) => (
            <ConversationRow
              key={r.executionId}
              run={r}
              displayName={flowNames.get(r.flowId) ?? r.displayName}
              onOpen={() => navigate(`/conversations/${encodeURIComponent(r.executionId)}`)}
            />
          ))}
        </div>
      )}

      {query.hasNextPage && (
        <div className="run-list-more">
          <button
            type="button"
            className="btn"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? <Spinner size={14} /> : <Icon name="chevron-down" size={14} />}{' '}
            {t.runs.loadMore}
          </button>
        </div>
      )}
    </div>
  )
}

function statusBadgeKind(status: RunStatus): string {
  switch (status) {
    case 'success':
      return 'success'
    case 'partial_success':
      return 'partial'
    case 'failure':
      return 'failure'
    case 'awaiting_user_response':
      return 'warning'
    default:
      return ''
  }
}

function ConversationRow({ run, displayName, onOpen }: { run: RunSummary; displayName: string; onOpen: () => void }) {
  const { t, formatAgo, formatDuration } = useI18n()
  return (
    <div className="run-row card" onClick={onOpen}>
      <div className="run-row-main">
        <div className="run-row-title">{displayName}</div>
        {run.briefPreview && <div className="run-row-brief muted">{run.briefPreview}</div>}
        <div className="run-row-meta">
          <span className="chip mono">{run.executionId.slice(0, 16)}…</span>
          <span className="muted">{formatAgo(run.startedAt)}</span>
          {run.durationMs != null && <span className="muted">· {formatDuration(run.durationMs)}</span>}
          {run.startedBy && <span className="muted">· {run.startedBy}</span>}
        </div>
      </div>
      <div className="run-row-right">
        {run.filesOut != null && run.filesOut > 0 && (
          <span className="chip" title={t.runs.deliverableFilesTitle(run.filesOut)}>
            <Icon name="download" size={11} /> {run.filesOut}
          </span>
        )}
        <Badge kind={statusBadgeKind(run.status)} dot>
          {t.runs.statusLabel(run.status)}
        </Badge>
        <Icon name="chevron" size={14} />
      </div>
    </div>
  )
}
